import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { analysisAPI } from '../services/api';
import { Download, FileText, Database } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { Link  } from 'react-router-dom';
import { ArrowLeft, XCircle, Image } from 'lucide-react';

/**
 * BatchVisualizations
 *
 * Route: /analysis/:id/visualizations  (legacy results page)
 *
 * Displays the output files produced by the Nextflow + R pipeline for a single
 * analysis run. Results are split into two sections:
 *   - Visualizations: PDF plots rendered as in-page PNG previews (downloaded
 *     as authenticated blob URLs to bypass nginx auth on static files).
 *   - Data Files: downloadable CSVs, QZA artifacts, and taxonomy files.
 *
 * Note: This page pre-dates Epi2meResults and is kept for backwards compatibility.
 * New runs are directed to /analysis/:id/epi2me instead.
 *
 * Side effects:
 *   - Fetches result file metadata on mount.
 *   - Fetches PNG thumbnails as blob URLs; revokes them on unmount to avoid leaks.
 */
const BatchVisualizations = () => {
  const { id } = useParams();
  // List of result file metadata objects returned by the API.
  const [resultFiles, setResultFiles] = useState([]);
  // True while the initial file list is loading.
  const [loading, setLoading] = useState(true);
  // Error message for the file list fetch failure.
  const [error, setError] = useState(null);
  // Map of PNG filename → object URL, used to render in-page image previews.
  const [imageUrls, setImageUrls] = useState({});
  // Transient error message shown when a file download fails (auto-clears after 5 s).
  const [downloadError, setDownloadError] = useState('');

  // Fetch the list of result files for this analysis on mount.
  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await analysisAPI.getResults(id);
        setResultFiles(response.data.result_files || []);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [id]);

  // Fetch images with authentication and create blob URLs
  // For each PDF plot, attempt to download the matching .png thumbnail.
  // Blob URLs are created so the authenticated response can be rendered in <img> tags.
  // Any PNG that doesn't exist is silently skipped; the PDF download remains available.
  useEffect(() => {
    const fetchImages = async () => {
      const pdfFiles = resultFiles.filter(f => f.filename.endsWith('.pdf'));
      const urls = {};

      for (const file of pdfFiles) {
        const pngFilename = file.filename.replace('.pdf', '.png');
        try {
          const response = await analysisAPI.downloadResultFile(id, pngFilename);
          const blob = new Blob([response.data], { type: 'image/png' });
          urls[pngFilename] = URL.createObjectURL(blob);
        } catch (err) {
          // console.log(`PNG not found for ${file.filename}, will use PDF fallback`);
        }
      }

      setImageUrls(urls);
    };

    if (resultFiles.length > 0) {
      fetchImages();
    }

    // Cleanup blob URLs on unmount to free browser memory.
    return () => {
      Object.values(imageUrls).forEach(url => URL.revokeObjectURL(url));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resultFiles, id]);

  /**
   * Downloads a result file by creating a temporary <a> element and
   * programmatically clicking it. The blob approach ensures the download
   * works even when the file is served behind authentication.
   *
   * @param {string} filename - The filename as returned by the API.
   */
  const handleDownload = async (filename) => {
    try {
      const response = await analysisAPI.downloadResultFile(id, filename);
      // Create blob from response
      const blob = new Blob([response.data]);
      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Download failed:', error);
      setDownloadError('Failed to download file. Please try again.');
      setTimeout(() => setDownloadError(''), 5000);  // Clear after 5 seconds
    }

  };

  // Returns an appropriate icon for a given filename based on its extension.
  const getFileIcon = (filename) => {
    if (filename.endsWith('.csv')) return <Database className="h-4 w-4" />;
    if (filename.endsWith('.qza')) return <Database className="h-4 w-4" />;
    return <FileText className="h-4 w-4" />;
  };

  // Separate files into visualizations and data files
  // PDFs are treated as visualizations; other structured data formats are data files.
  const visualizationFiles = resultFiles.filter(f =>
    f.filename.endsWith('.pdf')
  );

  const dataFiles = resultFiles.filter(f =>
    f.filename.endsWith('.csv') ||
    f.filename.endsWith('.qza') ||
    f.filename.endsWith('.shared') ||
    f.filename.endsWith('.taxonomy')
  );

  if (loading) return <div className="p-8">Loading visualizations...</div>;
  if (error) return <div className="p-8 text-red-600">Error: {error}</div>;

  return (
    <div className="p-8 max-w-6xl mx-auto space-y-8">
      {/* Back to dashboard */}
      <div className="flex items-center gap-4 mb-4">
        <Link to="/dashboard">
          <Button variant="ghost" size="sm">
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Dashboard
          </Button>
        </Link>
      </div>


      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold mb-2">Results</h1>
        <p className="text-gray-600">Analysis ID: {id}</p>
      </div>

      {downloadError && (
        <div className="bg-destructive/10 border border-destructive/20 text-destructive p-3 rounded-lg flex items-center gap-2">
          <XCircle className="h-4 w-4" />
          {downloadError}
        </div>
      )}
      {/* Visualizations Section */}
      {visualizationFiles.length > 0 && (
        <div className="space-y-6">
          <h2 className="text-2xl font-semibold">Analysis Visualizations</h2>
          {visualizationFiles.map((file, index) => (
            <Card key={index}>
              <CardHeader>
                <CardTitle className="flex items-center justify-between">
                  <span>{file.filename.replace('.pdf', '').replace(/_/g, ' ').toUpperCase()}</span>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleDownload(file.filename)}
                  >
                    <Download className="h-4 w-4 mr-1" />
                    Download PDF
                  </Button>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="border rounded-lg overflow-hidden bg-white p-4">
                  {/* Show the PNG preview if available; otherwise show a placeholder. */}
                  {imageUrls[file.filename.replace('.pdf', '.png')] ? (
                    <img
                      src={imageUrls[file.filename.replace('.pdf', '.png')]}
                      alt={file.filename}
                      className="w-full h-auto"
                    />
                  ) : (
                    <div className="text-center py-8 text-gray-500">
                      <p>PNG visualization not available</p>
                      <p className="text-sm mt-2">Download PDF to view this visualization</p>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Data Files Section */}
      {dataFiles.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Database className="h-5 w-5" />
              Download Data Files
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid gap-3">
              {dataFiles.map((file, index) => (
                <div
                  key={index}
                  className="flex items-center justify-between p-3 bg-muted/50 rounded-lg"
                >
                  <div className="flex items-center gap-3">
                    {getFileIcon(file.filename)}
                    <div>
                      <p className="font-medium text-sm">{file.filename}</p>
                      {file.description && (
                        <p className="text-xs text-muted-foreground">{file.description}</p>
                      )}
                    </div>
                  </div>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleDownload(file.filename)}
                  >
                    <Download className="h-4 w-4 mr-1" />
                    Download
                  </Button>
                </div>
              ))}
            </div>
          </CardContent>
      </Card>
      )}

      {/* No results message */}
      {visualizationFiles.length === 0 && dataFiles.length === 0 && (
        <Card>
          <CardContent className="py-12 text-center">
            <Image className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
            <h3 className="text-lg font-medium mb-2">No visualizations yet</h3>
            <p className="text-muted-foreground">
              Results will appear here once processing is complete
            </p>
          </CardContent>
        </Card>
      )}

    </div>
  );
};

export default BatchVisualizations;
